"use strict";
/**
 * Appointment API Service
 * -----------------------------------------------------------------------------
 * Handles all appointment + availability operations between Vue 3 and your PHP
 * backend using axios. Every method documents the PHP endpoint it calls and the
 * MySQL CRUD syntax the endpoint should run.
 *
 * The service ALSO contains a pure, framework-agnostic availability calculator
 * (`computeAvailableSlots`) so you can see exactly how open slots are derived
 * from: weekly availability template - time off - already-booked appointments.
 * Your PHP endpoint runs the same logic server-side; this client copy is used
 * for the demo + as a reference implementation.
 * -----------------------------------------------------------------------------
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.caregiverPairCompat = exports.caregiverContextCompat = exports.patientContextViability = exports.ORDER = exports.CAREGIVER_TYPE_LABELS = exports.CAREGIVER_TYPES = exports.CAREGIVER_CONTEXTS = exports.PATIENT_CONTEXTS = void 0;
exports.buildNotifyPayload = buildNotifyPayload;
exports.resolveGroupMembers = resolveGroupMembers;
exports.isContextHard = isContextHard;
exports.caregiverTypeForCredential = caregiverTypeForCredential;
exports.visitContextsForCaregiverType = visitContextsForCaregiverType;
exports.rubricContext = rubricContext;
exports.evaluateBooking = evaluateBooking;
exports.evaluateVisitViability = evaluateVisitViability;
exports.resolveDaySegments = resolveDaySegments;
exports.computeAvailableSlots = computeAvailableSlots;
exports.findPatientGroups = findPatientGroups;
exports.getAvailability = getAvailability;
exports.createAppointment = createAppointment;
exports.generateSeriesOccurrences = generateSeriesOccurrences;
exports.createAppointmentSeries = createAppointmentSeries;
exports.updateAppointment = updateAppointment;
exports.setAppointmentStatus = setAppointmentStatus;
exports.cancelAppointment = cancelAppointment;
exports.deleteAppointment = deleteAppointment;
exports.getBookedTimesForProvider = getBookedTimesForProvider;
exports.getAppointmentsForPatients = getAppointmentsForPatients;
exports.getAvailabilityRules = getAvailabilityRules;
exports.saveAvailabilityRule = saveAvailabilityRule;
exports.deleteAvailabilityRule = deleteAvailabilityRule;
exports.getTimeOff = getTimeOff;
exports.saveTimeOff = saveTimeOff;
exports.deleteTimeOff = deleteTimeOff;
exports.getSchedulingNurseStaff = getSchedulingNurseStaff;
exports.getNurseShiftSettings = getNurseShiftSettings;
exports.saveNurseShiftSettings = saveNurseShiftSettings;
exports.getSchedulingUiSettings = getSchedulingUiSettings;
exports.saveSchedulingUiSettings = saveSchedulingUiSettings;
var axios_1 = require("axios");
var apis_1 = require("@/constants/apis");
// In production point this at your PHP API root (e.g. https://app.clinic.com/api)
var API_BASE_URL = apis_1.CURR_API; // '/api'
// Flip to false once your PHP endpoints are live to use real axios calls.
var USE_MOCK = true;
var MOCK_DELAY = 400;
var delay = function (ms) {
    if (ms === void 0) { ms = MOCK_DELAY; }
    return new Promise(function (resolve) { return setTimeout(resolve, ms); });
};
/**
 * Turn a UI-level notify choice into the legacy notifyEmail/notifySms/notifyGroup
 * shape the appointment endpoints expect. Returns an "off" payload when notify
 * is undefined or no recipient is selected, so nothing gets sent.
 */
function buildNotifyPayload(notify, patient, caregiver) {
    var _a;
    var hasGroupMembers = !!((_a = notify === null || notify === void 0 ? void 0 : notify.groupMembers) === null || _a === void 0 ? void 0 : _a.length);
    if (!notify || (!notify.notifyPatient && !notify.notifyCaregiver && !hasGroupMembers)) {
        return { notifyEmail: false, notifySms: false, notifyGroup: [] };
    }
    var notifyGroup = [];
    if (notify.notifyPatient)
        notifyGroup.push(patient);
    if (notify.notifyCaregiver)
        notifyGroup.push(caregiver);
    if (notify.groupMembers)
        notifyGroup.push.apply(notifyGroup, notify.groupMembers);
    return {
        notifyEmail: notify.method === 'email',
        notifySms: notify.method === 'sms',
        notifyGroup: notifyGroup
    };
}
/**
 * Resolve a native <select multiple> value list (member emails, or the "All"
 * sentinel option) back to {name, email, phone} recipients. Shared by the
 * patient card's quick-scheduling group-notify picker (AppointmentModule.vue)
 * and the Scheduling Calendar's, so both read the exact same selection logic.
 */
function resolveGroupMembers(groupOptions, selectedValues) {
    var allMembers = groupOptions.flatMap(function (group) { return group.members; });
    if (selectedValues.includes('All')) {
        return allMembers.map(function (m) { return ({ name: m.patientName, email: m.email, phone: m.phone }); });
    }
    var selected = new Set(selectedValues);
    return allMembers
        .filter(function (m) { return selected.has(m.email); })
        .map(function (m) { return ({ name: m.patientName, email: m.email, phone: m.phone }); });
}
exports.PATIENT_CONTEXTS = [
    'Personal Routine', 'Social Presence', 'Unavailable / Private',
    'Meals & Nutrition', 'Out of Home', 'Sleep / Rest'
];
exports.CAREGIVER_CONTEXTS = [
    'In-Home', 'Out-of-Home Escort', 'Out-of-Home Medical', 'Telehealth',
    'Facility Visit', 'Transportation', 'Community Program'
];
exports.CAREGIVER_TYPES = [
    { key: 'md', label: 'Physician', abbr: 'MD' },
    { key: 'rn', label: 'Nursing', abbr: 'RN' },
    { key: 'hha', label: 'Home Health Aide', abbr: 'HHA' },
    { key: 'pt', label: 'Physical Therapy', abbr: 'PT' },
    { key: 'ot', label: 'Occupational Therapy', abbr: 'OT' },
    { key: 'slp', label: 'Speech Therapy', abbr: 'SLP' },
    { key: 'sw', label: 'Social Worker', abbr: 'SW' },
    { key: 'bh', label: 'Behavioral Health', abbr: 'BH' },
    { key: 'comp', label: 'Companion / Sitter', abbr: 'Comp' },
    { key: 'cm', label: 'Care Manager', abbr: 'CM' },
    { key: 'resp', label: 'Respite Care', abbr: 'Resp' }
];
exports.CAREGIVER_TYPE_LABELS = Object.fromEntries(exports.CAREGIVER_TYPES.map(function (t) { return [t.key, t.label]; }));
exports.ORDER = { no: 0, conditional: 1, yes: 2 };
var Y = 'yes', C = 'conditional', N = 'no';
/** Matrix 1 — Patient Context × Caregiver Context
 *  'Out-of-Home Medical' follows the same pattern as the other physically-away
 *  contexts (Escort/Facility Visit/Transportation/Community Program): it only
 *  makes sense if the patient's own context says they're actually out of the
 *  home — everywhere else it contradicts where the patient actually is. */
exports.patientContextViability = {
    'Personal Routine': { 'In-Home': Y, 'Out-of-Home Escort': N, 'Out-of-Home Medical': N, 'Telehealth': Y, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    'Social Presence': { 'In-Home': C, 'Out-of-Home Escort': N, 'Out-of-Home Medical': N, 'Telehealth': C, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    'Unavailable / Private': { 'In-Home': N, 'Out-of-Home Escort': N, 'Out-of-Home Medical': N, 'Telehealth': C, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    'Meals & Nutrition': { 'In-Home': C, 'Out-of-Home Escort': N, 'Out-of-Home Medical': N, 'Telehealth': C, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    'Out of Home': { 'In-Home': N, 'Out-of-Home Escort': Y, 'Out-of-Home Medical': Y, 'Telehealth': C, 'Facility Visit': Y, 'Transportation': Y, 'Community Program': Y },
    'Sleep / Rest': { 'In-Home': C, 'Out-of-Home Escort': N, 'Out-of-Home Medical': N, 'Telehealth': N, 'Facility Visit': N, 'Transportation': N, 'Community Program': N }
};
/** Matrix 2 — Caregiver Type × Caregiver Context
 *  'Out-of-Home Medical' = the caregiver delivers actual clinical/skilled care
 *  at a location other than the patient's home or the caregiver's own
 *  facility (hospital rounds, SNF/rehab visit, ER accompaniment, dialysis
 *  center, etc.) — distinct from 'Facility Visit' (patient comes to the
 *  caregiver's own office) and 'Out-of-Home Escort' (non-clinical
 *  accompaniment/transport). Rubric: full "yes" for roles that routinely
 *  deliver skilled care in outside facilities (MD, RN, and — per this org's
 *  pattern of already trusting them with Telehealth — SW for care-coordination
 *  work done on-site); "conditional" for allied therapy/behavioral-health
 *  disciplines whose ability to flex into an outside facility is
 *  credentialing-dependent, and for Care Manager (coordinates/may attend, but
 *  isn't delivering the clinical care itself); "no" for non-clinical,
 *  custodial roles with no scope to deliver medical care off-site (HHA,
 *  Companion/Sitter, Respite Care). */
exports.caregiverContextCompat = {
    md: { 'In-Home': C, 'Out-of-Home Escort': N, 'Out-of-Home Medical': Y, 'Telehealth': Y, 'Facility Visit': Y, 'Transportation': N, 'Community Program': N },
    rn: { 'In-Home': Y, 'Out-of-Home Escort': N, 'Out-of-Home Medical': Y, 'Telehealth': C, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    hha: { 'In-Home': Y, 'Out-of-Home Escort': C, 'Out-of-Home Medical': N, 'Telehealth': N, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    pt: { 'In-Home': Y, 'Out-of-Home Escort': N, 'Out-of-Home Medical': C, 'Telehealth': C, 'Facility Visit': C, 'Transportation': N, 'Community Program': N },
    ot: { 'In-Home': Y, 'Out-of-Home Escort': N, 'Out-of-Home Medical': C, 'Telehealth': C, 'Facility Visit': C, 'Transportation': N, 'Community Program': N },
    slp: { 'In-Home': C, 'Out-of-Home Escort': N, 'Out-of-Home Medical': C, 'Telehealth': C, 'Facility Visit': C, 'Transportation': N, 'Community Program': N },
    sw: { 'In-Home': Y, 'Out-of-Home Escort': N, 'Out-of-Home Medical': Y, 'Telehealth': Y, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    bh: { 'In-Home': C, 'Out-of-Home Escort': N, 'Out-of-Home Medical': C, 'Telehealth': C, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    comp: { 'In-Home': Y, 'Out-of-Home Escort': C, 'Out-of-Home Medical': N, 'Telehealth': Y, 'Facility Visit': N, 'Transportation': C, 'Community Program': N },
    cm: { 'In-Home': Y, 'Out-of-Home Escort': N, 'Out-of-Home Medical': C, 'Telehealth': Y, 'Facility Visit': N, 'Transportation': N, 'Community Program': N },
    resp: { 'In-Home': Y, 'Out-of-Home Escort': C, 'Out-of-Home Medical': N, 'Telehealth': Y, 'Facility Visit': N, 'Transportation': C, 'Community Program': N }
};
/** Matrix 3 — Caregiver × Caregiver (parallel visits). Symmetric; BH is strictest. */
exports.caregiverPairCompat = {
    md: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: C, comp: Y, cm: Y, resp: Y },
    rn: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: C, comp: Y, cm: Y, resp: Y },
    hha: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: C, sw: Y, bh: C, comp: Y, cm: Y, resp: Y },
    pt: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: N, comp: Y, cm: Y, resp: Y },
    ot: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: N, comp: Y, cm: Y, resp: Y },
    slp: { md: Y, rn: Y, hha: C, pt: Y, ot: Y, slp: Y, sw: Y, bh: N, comp: Y, cm: Y, resp: Y },
    sw: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: C, comp: Y, cm: Y, resp: Y },
    bh: { md: C, rn: C, hha: C, pt: N, ot: N, slp: N, sw: C, bh: C, comp: C, cm: C, resp: C },
    comp: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: C, comp: Y, cm: Y, resp: Y },
    cm: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: C, comp: Y, cm: Y, resp: Y },
    resp: { md: Y, rn: Y, hha: Y, pt: Y, ot: Y, slp: Y, sw: Y, bh: C, comp: Y, cm: Y, resp: Y }
};
/** Only "Out of Home" is inherently hard (patient physically absent). */
function isContextHard(context) {
    return context === 'Out of Home';
}
/**
 * Best-effort mapping from a caregiver's category + credential/specialty text
 * to one of the 11 CaregiverType keys used by the matrices above.
 */
function caregiverTypeForCredential(credential, category) {
    var text = "".concat(credential !== null && credential !== void 0 ? credential : '').toLowerCase();
    if (category === 'nurse')
        return 'rn';
    if (category === 'hha')
        return 'hha';
    if (category === 'provider')
        return 'md';
    // allied — match specialty text against known therapy/support types
    if (/physical therap/.test(text) || text === 'pt')
        return 'pt';
    if (/occupational therap/.test(text) || text === 'ot')
        return 'ot';
    if (/speech/.test(text) || text === 'slp')
        return 'slp';
    if (/social work/.test(text) || text === 'sw')
        return 'sw';
    if (/behavioral|mental health/.test(text) || text === 'bh')
        return 'bh';
    if (/companion|sitter/.test(text))
        return 'comp';
    if (/care manag/.test(text) || text === 'cm')
        return 'cm';
    if (/respite/.test(text) || text === 'resp')
        return 'resp';
    return 'comp';
}
/** Which visit contexts a given caregiver type can offer at all (yes or conditional). */
function visitContextsForCaregiverType(type) {
    return exports.CAREGIVER_CONTEXTS.filter(function (ctx) { return exports.caregiverContextCompat[type][ctx] !== 'no'; });
}
/**
 * Admin-defined custom contexts (Administration -> Customization -> In-Home
 * Visit Context, scheduling_custom_contexts table) aren't in the static
 * CAREGIVER_CONTEXTS list, so they have no row of their own in either
 * compatibility matrix. Per stakeholder direction, every custom context is
 * treated as 'Out-of-Home Medical' for rubric purposes — this is the one
 * place that mapping happens; both matrix lookups in evaluateBooking() and
 * the compat-verdict badge in SchedulingCalendar.vue go through this instead
 * of indexing the matrices directly.
 */
function rubricContext(context) {
    return exports.CAREGIVER_CONTEXTS.includes(context)
        ? context
        : 'Out-of-Home Medical';
}
/**
 * Live viability check for the New Visit / Reschedule modals — combines the
 * patient's applicable unavailable-block(s) for the requested time with the
 * caregiver-type × visit-context matrix.
 * @param patientBlocks blocks (already resolved for the target date) whose
 *   time range overlaps the requested appointment start time
 */
function evaluateBooking(params) {
    var _a, _b, _c, _d;
    var patientBlocks = params.patientBlocks, caregiverType = params.caregiverType, caregiverContext = params.caregiverContext, slotStartMin = params.slotStartMin, slotEndMin = params.slotEndMin, caregiverWindows = params.caregiverWindows, caregiverTimeOff = params.caregiverTimeOff;
    var reasons = [];
    var worst = 'yes';
    var rubric = rubricContext(caregiverContext);
    var isTelehealth = rubric === 'Telehealth';
    if (slotStartMin != null && slotEndMin != null) {
        if (caregiverWindows && !caregiverWindows.some(function (w) { return w.start <= slotStartMin && w.end >= slotEndMin; })) {
            return { decision: 'blocked', reasons: ['Caregiver is not scheduled to work during this time.'] };
        }
        if (caregiverTimeOff && caregiverTimeOff.some(function (o) { return o.start < slotEndMin && o.end > slotStartMin; })) {
            return { decision: 'blocked', reasons: ['Caregiver has time off during this slot.'] };
        }
    }
    for (var _i = 0, patientBlocks_1 = patientBlocks; _i < patientBlocks_1.length; _i++) {
        var block = patientBlocks_1[_i];
        var label = block.context_label;
        if (block.exclusivity === 'hard' && !isTelehealth) {
            reasons.push("Patient is unavailable (".concat(label, ", ").concat(block.start_time, "\u2013").concat(block.end_time, ") \u2014 hard block, not compatible with an in-person visit"));
            return { decision: 'blocked', reasons: reasons };
        }
        var level = (_b = (_a = exports.patientContextViability[label]) === null || _a === void 0 ? void 0 : _a[rubric]) !== null && _b !== void 0 ? _b : 'yes';
        if (exports.ORDER[level] < exports.ORDER[worst])
            worst = level;
        if (level !== 'yes') {
            reasons.push("".concat(label, " is ").concat(level === 'no' ? 'blocked' : 'conditional', " for a ").concat(caregiverContext, " visit"));
        }
    }
    var typeLevel = (_d = (_c = exports.caregiverContextCompat[caregiverType]) === null || _c === void 0 ? void 0 : _c[rubric]) !== null && _d !== void 0 ? _d : 'yes';
    if (exports.ORDER[typeLevel] < exports.ORDER[worst])
        worst = typeLevel;
    if (typeLevel !== 'yes') {
        reasons.push("".concat(exports.CAREGIVER_TYPE_LABELS[caregiverType], " + ").concat(caregiverContext, " is ").concat(typeLevel === 'no' ? 'not typically allowed' : 'conditional'));
    }
    if (worst === 'no')
        return { decision: 'blocked', reasons: reasons };
    if (worst === 'conditional')
        return { decision: 'conditional', reasons: reasons };
    return { decision: 'allowed', reasons: reasons };
}
var VIABILITY_DECISION_RANK = { blocked: 0, conditional: 1, allowed: 2 };
/**
 * Full live-booking viability check — overlap with this caregiver's own other
 * visits, cross-caregiver parallel-visit compatibility (caregiverPairCompat),
 * the concurrent-caregiver-per-patient cap, and evaluateBooking()'s
 * patient-block/context compatibility. Extracted from SchedulingCalendar.vue's
 * evaluateBookingSlot() so both it and any other booking UI (e.g. a patient's
 * own Master Calendar booking modal) share one implementation instead of two
 * copies that can silently drift out of sync.
 */
function evaluateVisitViability(params) {
    var _a, _b, _c;
    var date = params.date, time = params.time, duration = params.duration, _d = params.excludeIds, excludeIds = _d === void 0 ? new Set() : _d, caregiverType = params.caregiverType, caregiverContext = params.caregiverContext, selectedCaregiverId = params.selectedCaregiverId, caregiverAppointments = params.caregiverAppointments, patientAppointments = params.patientAppointments, resolveCaregiverType = params.resolveCaregiverType, maxConcurrentCaregivers = params.maxConcurrentCaregivers, dayBlocks = params.dayBlocks, caregiverWindows = params.caregiverWindows, caregiverTimeOff = params.caregiverTimeOff;
    if (!date || !time || !caregiverType || !caregiverContext) {
        return { decision: 'allowed', reasons: [] };
    }
    var startMin = toMinutes(time);
    var endMin = startMin + duration;
    var overlapsExistingVisit = caregiverAppointments.some(function (a) {
        var _a;
        if (a.date !== date || a.status === 'cancelled')
            return false;
        if (excludeIds.has(a.id))
            return false;
        var aStart = toMinutes(a.time);
        var aEnd = aStart + ((_a = a.durationMinutes) !== null && _a !== void 0 ? _a : 60);
        return startMin < aEnd && aStart < endMin;
    });
    if (overlapsExistingVisit) {
        return { decision: 'blocked', reasons: ['This time overlaps another visit already booked with this caregiver.'] };
    }
    // Cross-caregiver parallel-visit compatibility: this patient may already have
    // a DIFFERENT caregiver's visit overlapping this time — check caregiverPairCompat
    // rather than silently allowing (or silently blocking) every combination.
    var pairDecision = 'allowed';
    var pairReasons = [];
    // Distinct caregivers already overlapping this patient/time — tracked
    // separately from the pairwise check below, since the matrix only answers
    // "can A overlap with B" and never caps the total headcount. Three mutually
    // "yes" pairs would otherwise stack to 3+ caregivers in the room at once.
    var overlappingCaregiverIds = new Set();
    for (var _i = 0, patientAppointments_1 = patientAppointments; _i < patientAppointments_1.length; _i++) {
        var other = patientAppointments_1[_i];
        if (other.date !== date || other.status === 'cancelled')
            continue;
        if (excludeIds.has(other.id))
            continue;
        if (other.providerId === selectedCaregiverId)
            continue; // same-caregiver case already handled above
        var otherStart = toMinutes(other.time);
        var otherEnd = otherStart + ((_a = other.durationMinutes) !== null && _a !== void 0 ? _a : 60);
        if (!(startMin < otherEnd && otherStart < endMin))
            continue;
        overlappingCaregiverIds.add(other.providerId);
        var otherType = resolveCaregiverType(other.providerId);
        if (!otherType)
            continue;
        var level = (_c = (_b = exports.caregiverPairCompat[caregiverType]) === null || _b === void 0 ? void 0 : _b[otherType]) !== null && _c !== void 0 ? _c : 'no';
        var otherLabel = exports.CAREGIVER_TYPE_LABELS[otherType];
        if (level === 'no') {
            pairReasons.push("".concat(otherLabel, " already has an overlapping visit with this patient \u2014 ").concat(otherLabel, " and ").concat(exports.CAREGIVER_TYPE_LABELS[caregiverType], " cannot see this patient in parallel."));
            pairDecision = 'blocked';
        }
        else if (level === 'conditional' && pairDecision !== 'blocked') {
            pairReasons.push("".concat(otherLabel, " already has an overlapping visit with this patient \u2014 parallel visits between ").concat(otherLabel, " and ").concat(exports.CAREGIVER_TYPE_LABELS[caregiverType], " require coordination."));
            pairDecision = 'conditional';
        }
    }
    // Concurrent-caregiver cap — a hard, independent gate. Fires even when every
    // individual pair above is "yes," since real-world visits rarely have more
    // than a couple of caregivers physically present with a patient at once.
    var concurrentCount = overlappingCaregiverIds.size + 1;
    if (maxConcurrentCaregivers > 0 && concurrentCount > maxConcurrentCaregivers) {
        return {
            decision: 'blocked',
            reasons: ["This patient already has ".concat(overlappingCaregiverIds.size, " caregiver").concat(overlappingCaregiverIds.size === 1 ? '' : 's', " scheduled during this time \u2014 the practice limit is ").concat(maxConcurrentCaregivers, " concurrent caregiver").concat(maxConcurrentCaregivers === 1 ? '' : 's', " per patient. Adjust the limit in Settings if this is expected.")]
        };
    }
    // Overlap against the visit's full [startMin, endMin) range, in minutes —
    // not a raw string compare, since block times come back "HH:mm:ss" while
    // bookingTime is "HH:mm"; comparing those as strings mishandles boundaries
    // (e.g. a block ending exactly at the visit's start incorrectly still matched).
    var filteredDayBlocks = dayBlocks.filter(function (b) {
        var bStart = toMinutes(b.start_time);
        var bEnd = toMinutes(b.end_time);
        return startMin < bEnd && bStart < endMin;
    });
    var baseResult = evaluateBooking({
        patientBlocks: filteredDayBlocks,
        caregiverType: caregiverType,
        caregiverContext: caregiverContext,
        slotStartMin: startMin,
        slotEndMin: endMin,
        caregiverWindows: caregiverWindows,
        caregiverTimeOff: caregiverTimeOff
    });
    var finalDecision = VIABILITY_DECISION_RANK[pairDecision] <= VIABILITY_DECISION_RANK[baseResult.decision] ? pairDecision : baseResult.decision;
    return { decision: finalDecision, reasons: __spreadArray(__spreadArray([], pairReasons, true), baseResult.reasons, true) };
}
/* ============================================================================
 * AVAILABILITY CALCULATOR  (the core logic you asked about)
 * ----------------------------------------------------------------------------
 * Given a provider's weekly rules, their time-off, and the appointments already
 * booked on a date, return every slot for that date flagged available/unavailable.
 *
 * This is the SAME algorithm your PHP endpoint should run. The PHP version just
 * pulls the three inputs from MySQL instead of receiving them as arguments.
 * ========================================================================== */
// Convert "HH:mm" -> minutes since midnight (handy for range math)
function toMinutes(time) {
    var _a = time.split(':').map(Number), h = _a[0], m = _a[1];
    return h * 60 + m;
}
// Convert minutes since midnight -> "HH:mm"
function toTimeStr(mins) {
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    return "".concat(String(h).padStart(2, '0'), ":").concat(String(m).padStart(2, '0'));
}
// Pretty 12h label, e.g. "9:30 AM"
function toLabel(time) {
    var _a = time.split(':').map(Number), h = _a[0], m = _a[1];
    var period = h >= 12 ? 'PM' : 'AM';
    var displayH = h % 12 === 0 ? 12 : h % 12;
    return "".concat(displayH, ":").concat(String(m).padStart(2, '0'), " ").concat(period);
}
/**
 * Turns a day's applicable rules into ordinary (non-wrapping) minute ranges,
 * splitting any overnight rule (end <= start, e.g. 19:00-07:00 => rule.endTime
 * "07:00" <= rule.startTime "19:00") across the calendar-day boundary it
 * actually crosses:
 *   - a rule filed under `dayOfWeek` itself contributes its whole [start, end)
 *     window when it doesn't cross midnight, or just [start, 1440) — tonight's
 *     portion — when it does
 *   - a rule filed under the PREVIOUS day that is itself overnight contributes
 *     [0, end) — the early-morning spillover that lands on *this* day
 * Every consumer (slot generation, "Caregiver Unavailable" gap rendering) can
 * then treat every segment as an ordinary same-day range — no wraparound
 * arithmetic needed anywhere past this point. No extra data fetch required:
 * getAvailabilityRules() already returns all 7 days' rules in one call, and
 * the early-morning spillover's time-off/bookings are already dated as
 * *this* day in the existing single-date fetches.
 */
function resolveDaySegments(dayOfWeek, rules) {
    var prevDow = (dayOfWeek + 6) % 7;
    var segments = [];
    for (var _i = 0, rules_1 = rules; _i < rules_1.length; _i++) {
        var rule = rules_1[_i];
        var start = toMinutes(rule.startTime);
        var end = toMinutes(rule.endTime);
        if (rule.dayOfWeek === dayOfWeek) {
            if (end > start) {
                segments.push({ rule: rule, start: start, end: end });
            }
            else if (end < start) {
                segments.push({ rule: rule, start: start, end: 1440 }); // overnight — tonight's portion
            }
            // end === start is zero-length; contributes nothing
        }
        else if (rule.dayOfWeek === prevDow && end <= start && end > 0) {
            segments.push({ rule: rule, start: 0, end: end }); // yesterday's overnight rule, this morning's portion
        }
    }
    return segments;
}
/**
 * Pure availability computation — shared by providers, nurses, and HHAs.
 * @param dateStr             YYYY-MM-DD being requested
 * @param rules               weekly availability rows
 * @param timeOff             time-off rows
 * @param bookedTimes         "HH:mm" start-times already taken (pending/approved appts)
 * @param maxConsecutiveHours Optional cap (nurses/HHAs). When provided:
 *   - booked shifts consume cap budget first
 *   - remaining open shifts accumulate until cap hit → capped=true
 *   - last fitting shift gets warning=true
 */
function computeAvailableSlots(dateStr, rules, timeOff, bookedTimes, maxConsecutiveHours) {
    var _a;
    var _b = dateStr.split('-').map(Number), y = _b[0], mo = _b[1], d = _b[2];
    var dayOfWeek = new Date(y, mo - 1, d).getDay();
    var daySegments = resolveDaySegments(dayOfWeek, rules);
    var slots = [];
    var _loop_1 = function (rule, start, end) {
        var windowMinutes = end - start;
        // Clamp so a shift length longer than the day's window (e.g. a 12h shift
        // configured against an 8h window) still yields one bookable slot spanning
        // the whole window, instead of silently producing zero slots.
        var dur = Math.min(rule.slotMinutes, windowMinutes);
        var _loop_2 = function (t) {
            var value = toTimeStr(t);
            var endValue = toTimeStr(t + dur);
            // Interval-overlap time-off check (fixes point-in-slot bug for long shifts):
            // slot [t, t+dur) overlaps off [offStart, offEnd) iff t < offEnd && offStart < t+dur
            var blockedByTimeOff = timeOff.some(function (off) {
                if (off.offDate !== dateStr)
                    return false;
                if (!off.startTime || !off.endTime)
                    return true; // whole day off
                var offStart = toMinutes(off.startTime);
                var offEnd = toMinutes(off.endTime);
                return t < offEnd && offStart < t + dur;
            });
            var isBooked = bookedTimes.includes(value);
            slots.push({
                value: value,
                label: toLabel(value),
                endValue: endValue,
                endLabel: toLabel(endValue),
                durationMinutes: dur,
                booked: isBooked && !blockedByTimeOff,
                available: !blockedByTimeOff && !isBooked
            });
        };
        for (var t = start; t + dur <= end; t += dur) {
            _loop_2(t);
        }
    };
    for (var _i = 0, daySegments_1 = daySegments; _i < daySegments_1.length; _i++) {
        var _c = daySegments_1[_i], rule = _c.rule, start = _c.start, end = _c.end;
        _loop_1(rule, start, end);
    }
    // Sort chronologically + de-dupe by start time
    var seen = new Set();
    var sorted = slots
        .sort(function (a, b) { return toMinutes(a.value) - toMinutes(b.value); })
        .filter(function (s) { return (seen.has(s.value) ? false : seen.add(s.value)); });
    // Cap logic — only for nurses/HHAs when maxConsecutiveHours is provided.
    // Booked shifts consume cap budget first; remaining open shifts accumulate until cap.
    if (maxConsecutiveHours != null && maxConsecutiveHours > 0) {
        var capMinutes = maxConsecutiveHours * 60;
        var bookedMinutes = sorted
            .filter(function (s) { return s.booked; })
            .reduce(function (sum, s) { var _a; return sum + ((_a = s.durationMinutes) !== null && _a !== void 0 ? _a : 0); }, 0);
        var remaining = Math.max(0, capMinutes - bookedMinutes);
        var accumulated = 0;
        for (var _d = 0, sorted_1 = sorted; _d < sorted_1.length; _d++) {
            var slot = sorted_1[_d];
            if (!slot.available)
                continue; // booked/time-off — already handled
            var dur = (_a = slot.durationMinutes) !== null && _a !== void 0 ? _a : 0;
            if (accumulated + dur > remaining) {
                slot.available = false;
                slot.capped = true;
            }
            else {
                accumulated += dur;
                if (accumulated === remaining) {
                    slot.warning = true;
                }
            }
        }
    }
    return sorted;
}
/* ============================================================================
 * MOCK DATA (demo only — your PHP/MySQL replaces this)
 * ========================================================================== */
var mockAvailability = [
    // Dr. Sarah Mitchell (prov-001) — Mon–Fri 9–5, 30-min slots
    { id: 'av-1', accountnumber: '96005400', providerId: 'prov-001', dayOfWeek: 1, startTime: '09:00', endTime: '17:00', slotMinutes: 30 },
    { id: 'av-2', accountnumber: '96005400', providerId: 'prov-001', dayOfWeek: 2, startTime: '09:00', endTime: '17:00', slotMinutes: 30 },
    { id: 'av-3', accountnumber: '96005400', providerId: 'prov-001', dayOfWeek: 3, startTime: '09:00', endTime: '17:00', slotMinutes: 30 },
    { id: 'av-4', accountnumber: '96005400', providerId: 'prov-001', dayOfWeek: 4, startTime: '09:00', endTime: '17:00', slotMinutes: 30 },
    { id: 'av-5', accountnumber: '96005400', providerId: 'prov-001', dayOfWeek: 5, startTime: '09:00', endTime: '15:00', slotMinutes: 30 },
    // Dr. James Carter (prov-002) — Mon/Wed/Fri 8–4
    { id: 'av-6', accountnumber: '96005400', providerId: 'prov-002', dayOfWeek: 1, startTime: '08:00', endTime: '16:00', slotMinutes: 30 },
    { id: 'av-7', accountnumber: '96005400', providerId: 'prov-002', dayOfWeek: 3, startTime: '08:00', endTime: '16:00', slotMinutes: 30 },
    { id: 'av-8', accountnumber: '96005400', providerId: 'prov-002', dayOfWeek: 5, startTime: '08:00', endTime: '16:00', slotMinutes: 30 }
];
var mockTimeOff = [
    { id: 'to-1', providerId: 'prov-001', offDate: '2026-04-22', startTime: null, endTime: null, reason: 'Conference' },
    { id: 'to-2', providerId: 'prov-001', offDate: '2026-04-24', startTime: '12:00', endTime: '13:30', reason: 'Lunch / Admin' }
];
// Simulated already-booked slots keyed by `${providerId}|${date}`
var mockBooked = {
    'prov-001|2026-04-20': ['10:30'],
    'prov-001|2026-04-21': ['09:00', '09:30', '14:00'],
    'prov-001|2026-06-22': ['10:00', '10:30'],
    'prov-001|2026-06-23': ['09:00', '09:30', '14:00']
};
/* ============================================================================
 * API METHODS
 * ========================================================================== */
/**
 * GET available time slots for a provider on a date.
 *
 * PHP endpoint:  GET /api/availability?provider_id=prov-001&date=2026-04-20
 *
 * PHP/MySQL the endpoint runs:
 *   // 1. weekly template for the weekday
 *   SELECT start_time, end_time, slot_minutes
 *   FROM provider_availability
 *   WHERE provider_id = :pid AND day_of_week = :dow;
 *
 *   // 2. time off for that date
 *   SELECT start_time, end_time
 *   FROM provider_time_off
 *   WHERE provider_id = :pid AND off_date = :date;
 *
 *   // 3. already booked (block pending + approved)
 *   SELECT appointment_time
 *   FROM appointments
 *   WHERE provider_id = :pid AND appointment_date = :date
 *     AND status IN ('pending','approved');
 *
 *   // then run computeAvailableSlots() equivalent and return JSON.
 */
function findPatientGroups(patientId) {
    return __awaiter(this, void 0, void 0, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    payload = {
                        PatientGroups: {
                            API_Meth: "GetPatientGroups",
                            is_provider: false,
                            patient_id: patientId
                        }
                    };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("findPatientGroups response", res.data);
                            return res.data;
                        })
                            .catch(function (err) {
                            console.log("findPatientGroups error", err);
                            return null;
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
function getAvailability(accountnumber, providerId, date, maxConsecutiveHours) {
    return __awaiter(this, void 0, Promise, function () {
        var payload, rules, timeOff, booked, slots;
        var _this = this;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!USE_MOCK) return [3 /*break*/, 2];
                    payload = {
                        Schedule: {
                            API_Meth: "GetProviderAvailabilityById",
                            accountnumber: accountnumber,
                            providerid: providerId,
                            date: date
                        }
                    };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) { return __awaiter(_this, void 0, void 0, function () {
                            var rows, availability, rules, _a, timeOff, booked, slots;
                            return __generator(this, function (_b) {
                                switch (_b.label) {
                                    case 0:
                                        console.log("Availability Payload Response");
                                        console.log(res.data.records);
                                        rows = Array.isArray(res.data.records) ? res.data.records : Array.isArray(res.data.records) ? res.data.records : [];
                                        availability = rows.map(function (r) {
                                            var _a, _b, _c, _d, _e;
                                            return ({
                                                id: r.id != null ? String(r.id) : null,
                                                accountnumber: r.accountnumber,
                                                providerId: (_a = r.provider_id) !== null && _a !== void 0 ? _a : null,
                                                dayOfWeek: Number(r.day_of_week),
                                                startTime: (_c = (_b = r.start_time) === null || _b === void 0 ? void 0 : _b.slice(0, 5)) !== null && _c !== void 0 ? _c : null,
                                                endTime: (_e = (_d = r.end_time) === null || _d === void 0 ? void 0 : _d.slice(0, 5)) !== null && _e !== void 0 ? _e : null,
                                                slotMinutes: Number(r.slot_minutes),
                                            });
                                        });
                                        console.log("Mapped Availability:", availability);
                                        rules = availability.filter(function (r) { return r.providerId === providerId; });
                                        return [4 /*yield*/, Promise.all([
                                                getTimeOff(accountnumber, providerId),
                                                getBookedTimesForProvider(accountnumber, providerId, date)
                                            ])];
                                    case 1:
                                        _a = _b.sent(), timeOff = _a[0], booked = _a[1];
                                        slots = computeAvailableSlots(date, rules, timeOff, booked, maxConsecutiveHours);
                                        console.log('[v0] getAvailability', { providerId: providerId, date: date, openSlots: slots.filter(function (s) { return s.available; }).length });
                                        return [2 /*return*/, { success: true, providerId: providerId, date: date, slots: slots }];
                                }
                            });
                        }); })
                            .catch(function (err) {
                            console.log("PostAvailabilityError", err);
                            return { success: false, providerId: providerId, date: date, slots: [], error: String(err) };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: 
                // ---- Mock implementation ----
                return [4 /*yield*/, delay()];
                case 3:
                    // ---- Mock implementation ----
                    _a.sent();
                    rules = mockAvailability.filter(function (r) { return r.providerId === providerId; });
                    timeOff = mockTimeOff.filter(function (t) { return t.providerId === providerId; });
                    booked = mockBooked["".concat(providerId, "|").concat(date)] || [];
                    slots = computeAvailableSlots(date, rules, timeOff, booked);
                    console.log('[v0] getAvailability', { providerId: providerId, date: date, openSlots: slots.filter(function (s) { return s.available; }).length });
                    return [2 /*return*/, { success: true, providerId: providerId, date: date, slots: slots }];
            }
        });
    });
}
/** `{conditional: true}` on the bridge's answer to the API's 422 visit_conditional (bridgeMap.js); nothing otherwise. */
function conditionalFlag(data) {
    return data && data.viability === 'conditional' ? { conditional: true } : {};
}
/**
 * Create a new appointment (always starts as 'pending').
 *
 * PHP endpoint:  POST /api/appointments
 *
 * PHP/MySQL:
 *   INSERT INTO appointments
 *     (patient_id, provider_id, appointment_date, appointment_time,
 *      meeting_type, reason, status, scheduled_by, notify_email, notify_sms, created_at)
 *   VALUES
 *     (:patient_id, :provider_id, :date, :time, :meeting_type, :reason,
 *      'pending', :scheduled_by, :notify_email, :notify_sms, NOW());
 *
 *   // optional: keep patient card in sync
 *   UPDATE patients SET next_appt_id = LAST_INSERT_ID() WHERE id = :patient_id;
 */
function createAppointment(accountnumber, notificatonOpt, payload, actorId) {
    return __awaiter(this, void 0, Promise, function () {
        var requestPayload;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    requestPayload = {
                        Appointment: {
                            API_Meth: "CreateAppointment",
                            accountnumber: accountnumber,
                            patientId: payload.patientId,
                            // subaccountnumber: payload.patsubaccountnumber,
                            providerId: payload.providerId,
                            appointmentDate: payload.date,
                            appointmentTime: payload.time,
                            meetingType: payload.meetingType,
                            reason: payload.reason,
                            scheduledBy: payload.scheduledBy,
                            notifyEmail: payload.notifyEmail,
                            notifySms: payload.notifySms,
                            notifyGroup: notificatonOpt,
                            durationMinutes: (_a = payload.durationMinutes) !== null && _a !== void 0 ? _a : 60,
                            seriesId: payload.seriesId,
                            confirmConditional: payload.confirmConditional === true,
                            caregiverType: payload.caregiverType,
                            // Backend authorization (scheduling permission scoping): the
                            // already-authenticated caller, resolved server-side via
                            // SessionActor -- see keyon/tswebhook.php's CreateAppointment
                            // dispatch block.
                            actor_id: actorId,
                        }
                    };
                    console.log("createAppointment request", requestPayload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), requestPayload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            var _a;
                            console.log("createAppointment response", res.data);
                            if (res.data && res.data.code === 1) {
                                return { success: true, appointmentId: String(res.data.id) };
                            }
                            return __assign({ success: false, error: (_a = res.data) === null || _a === void 0 ? void 0 : _a.error }, conditionalFlag(res.data));
                        })
                            .catch(function (err) {
                            console.log("createAppointment error", err);
                            return { success: false, error: String(err) };
                        })];
                case 1: return [2 /*return*/, _b.sent()];
            }
        });
    });
}
var MAX_SERIES_OCCURRENCES = 180;
/**
 * Pure date-math — no API calls. Expands a recurrence rule into concrete
 * occurrence dates, capped so a pathological rule (e.g. daily, no end date)
 * can't generate an unbounded number of rows.
 */
function generateSeriesOccurrences(rule, cap) {
    var _a;
    if (cap === void 0) { cap = MAX_SERIES_OCCURRENCES; }
    var dates = [];
    var _b = rule.startDate.split('-').map(Number), sy = _b[0], sm = _b[1], sd = _b[2];
    var cursor = new Date(sy, sm - 1, sd);
    var endDateObj = rule.endType === 'date' && rule.endDate
        ? (function () { var _a = rule.endDate.split('-').map(Number), ey = _a[0], em = _a[1], ed = _a[2]; return new Date(ey, em - 1, ed); })()
        : null;
    var days = rule.recurrenceType === 'weekly' ? ((_a = rule.recurrenceDays) !== null && _a !== void 0 ? _a : []) : null;
    var maxScanDays = 3660; // ~10 years — safety bound independent of `cap`
    var scanned = 0;
    while (dates.length < cap && scanned < maxScanDays) {
        if (endDateObj && cursor > endDateObj)
            break;
        var matches = rule.recurrenceType === 'daily' || !!(days === null || days === void 0 ? void 0 : days.includes(cursor.getDay()));
        if (matches) {
            dates.push("".concat(cursor.getFullYear(), "-").concat(String(cursor.getMonth() + 1).padStart(2, '0'), "-").concat(String(cursor.getDate()).padStart(2, '0')));
            if (rule.endType === 'count' && rule.endCount != null && dates.length >= rule.endCount)
                break;
        }
        cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
        scanned++;
    }
    return dates;
}
/**
 * Create a recurrence rule AND its visits, in one request.
 *
 * On main this recorded only the rule (keyon stored no time of day) and the
 * calendar then booked each occurrence through createAppointment. The port's
 * `POST /appointment-series` materialises the occurrences itself — checking
 * each against the caregiver's working hours, time off and diary, the
 * patient's caregiver cap and the rubric, and skipping the dates it cannot
 * book — so the caller sends the time of day and books nothing more: posting
 * the occurrences again would collide with the server's own rows. What comes
 * back is the visits that were created and the dates that were skipped, with
 * the server's reason for each.
 */
function createAppointmentSeries(accountnumber, payload, actorId) {
    return __awaiter(this, void 0, Promise, function () {
        var requestPayload;
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __generator(this, function (_j) {
            switch (_j.label) {
                case 0:
                    requestPayload = {
                        Appointment: {
                            API_Meth: 'CreateAppointmentSeries',
                            accountnumber: accountnumber,
                            patientId: payload.patientId,
                            providerId: payload.providerId,
                            meetingType: payload.meetingType,
                            durationMinutes: payload.durationMinutes,
                            reason: payload.reason,
                            recurrenceType: payload.recurrenceType,
                            recurrenceDays: (_b = (_a = payload.recurrenceDays) === null || _a === void 0 ? void 0 : _a.join(',')) !== null && _b !== void 0 ? _b : null,
                            startDate: payload.startDate,
                            appointmentTime: payload.time,
                            endType: payload.endType,
                            endCount: payload.endCount,
                            endDate: payload.endDate,
                            notifyEmail: (_d = (_c = payload.notify) === null || _c === void 0 ? void 0 : _c.notifyEmail) !== null && _d !== void 0 ? _d : false,
                            notifySms: (_f = (_e = payload.notify) === null || _e === void 0 ? void 0 : _e.notifySms) !== null && _f !== void 0 ? _f : false,
                            notifyGroup: (_h = (_g = payload.notify) === null || _g === void 0 ? void 0 : _g.notifyGroup) !== null && _h !== void 0 ? _h : [],
                            confirmConditional: payload.confirmConditional === true,
                            caregiverType: payload.caregiverType,
                            // Main's backend authorization input. The bridge drops it: the API takes
                            // the actor from the token (decision D4).
                            actor_id: actorId
                        }
                    };
                    console.log('createAppointmentSeries request', requestPayload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), requestPayload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 30000,
                        })
                            .then(function (res) {
                            var _a;
                            console.log('createAppointmentSeries response', res.data);
                            if (res.data && res.data.code === 1) {
                                return {
                                    success: true,
                                    seriesId: String(res.data.id),
                                    occurrences: Array.isArray(res.data.occurrences)
                                        ? res.data.occurrences.map(function (o) { return ({ id: String(o.id), date: String(o.date), time: String(o.time) }); })
                                        : [],
                                    skipped: Array.isArray(res.data.skipped)
                                        ? res.data.skipped.map(function (s) { return ({ date: String(s.date), reason: String(s.reason) }); })
                                        : []
                                };
                            }
                            return { success: false, error: (_a = res.data) === null || _a === void 0 ? void 0 : _a.error };
                        })
                            .catch(function (err) {
                            console.log('createAppointmentSeries error', err);
                            return { success: false, error: String(err) };
                        })];
                case 1: return [2 /*return*/, _j.sent()];
            }
        });
    });
}
/**
 * Update / reschedule an appointment.
 *
 * PHP endpoint:  Schedule via tswebhook.php, Appointment.API_Meth = UpdateAppointment
 *   UPDATE appointments
 *   SET appointment_date = :date, appointment_time = :time, provider_id = :provider_id,
 *       meeting_type = :meeting_type, reason = :reason, updated_at = NOW()
 *   WHERE id = :id AND accountnumber = :acnt;
 */
function updateAppointment(accountnumber_1, id_1, updates_1, actorId_1, notify_1) {
    return __awaiter(this, arguments, Promise, function (accountnumber, id, 
    // caregiverType: the new caregiver's rubric column, with a new caregiver
    // only (see createAppointment).
    updates, actorId, notify, options) {
        var requestPayload;
        var _a, _b, _c;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    requestPayload = {
                        Appointment: {
                            API_Meth: "UpdateAppointment",
                            accountnumber: accountnumber,
                            id: id,
                            patientId: updates.patientId,
                            appointmentDate: updates.date,
                            appointmentTime: updates.time,
                            providerId: updates.providerId,
                            meetingType: updates.meetingType,
                            reason: updates.reason,
                            durationMinutes: updates.durationMinutes,
                            notifyEmail: notify ? notify.notifyEmail : ((_a = updates.notifyEmail) !== null && _a !== void 0 ? _a : false),
                            notifySms: notify ? notify.notifySms : ((_b = updates.notifySms) !== null && _b !== void 0 ? _b : false),
                            notifyGroup: notify ? notify.notifyGroup : ((_c = updates.notifyGroupMembers) !== null && _c !== void 0 ? _c : []),
                            confirmConditional: options.confirmConditional === true,
                            caregiverType: updates.caregiverType,
                            // Backend authorization (scheduling permission scoping) -- see
                            // createAppointment's own actor_id comment above.
                            actor_id: actorId
                        }
                    };
                    console.log("updateAppointment request", requestPayload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), requestPayload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            var _a;
                            console.log("updateAppointment response", res.data);
                            var success = !!(res.data && res.data.code === 1);
                            return success ? { success: success } : __assign({ success: success, error: (_a = res.data) === null || _a === void 0 ? void 0 : _a.error }, conditionalFlag(res.data));
                        })
                            .catch(function (err) {
                            console.log("updateAppointment error", err);
                            return { success: false, error: String(err) };
                        })];
                case 1: return [2 /*return*/, _d.sent()];
            }
        });
    });
}
/**
 * Set an appointment's status (approve/cancel/complete).
 *
 * PHP endpoint:  Schedule via tswebhook.php, Appointment.API_Meth = SetAppointmentStatus
 *   UPDATE appointments SET status = :status, updated_at = NOW() WHERE id = :id AND accountnumber = :acnt;
 */
function setAppointmentStatus(accountnumber, id, status, actorId, notify) {
    return __awaiter(this, void 0, Promise, function () {
        var requestPayload;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    requestPayload = {
                        Appointment: {
                            API_Meth: "SetAppointmentStatus",
                            accountnumber: accountnumber,
                            id: id,
                            status: status,
                            notifyEmail: (_a = notify === null || notify === void 0 ? void 0 : notify.notifyEmail) !== null && _a !== void 0 ? _a : false,
                            notifySms: (_b = notify === null || notify === void 0 ? void 0 : notify.notifySms) !== null && _b !== void 0 ? _b : false,
                            notifyGroup: (_c = notify === null || notify === void 0 ? void 0 : notify.notifyGroup) !== null && _c !== void 0 ? _c : [],
                            // Backend authorization (scheduling permission scoping) -- see
                            // createAppointment's own actor_id comment above.
                            actor_id: actorId
                        }
                    };
                    console.log("setAppointmentStatus request", requestPayload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), requestPayload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            var _a;
                            console.log("setAppointmentStatus response", res.data);
                            return { success: !!(res.data && res.data.code === 1), error: (_a = res.data) === null || _a === void 0 ? void 0 : _a.error };
                        })
                            .catch(function (err) {
                            console.log("setAppointmentStatus error", err);
                            return { success: false, error: String(err) };
                        })];
                case 1: return [2 /*return*/, _d.sent()];
            }
        });
    });
}
/**
 * Cancel an appointment (soft cancel via status — keeps the row for history/audit).
 */
function cancelAppointment(accountnumber, id, actorId, notify) {
    return __awaiter(this, void 0, Promise, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, setAppointmentStatus(accountnumber, id, 'cancelled', actorId, notify)];
        });
    });
}
/**
 * Hard-delete an appointment row entirely.
 * PHP endpoint:  Schedule via tswebhook.php, Appointment.API_Meth = DeleteAppointment
 *   DELETE FROM appointments WHERE id = :id AND accountnumber = :acnt;
 */
function deleteAppointment(accountnumber, id, actorId) {
    return __awaiter(this, void 0, Promise, function () {
        var requestPayload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    requestPayload = {
                        Appointment: {
                            API_Meth: "DeleteAppointment",
                            accountnumber: accountnumber,
                            id: id,
                            // Backend authorization (scheduling permission scoping) -- see
                            // createAppointment's own actor_id comment above.
                            actor_id: actorId
                        }
                    };
                    console.log("deleteAppointment request", requestPayload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), requestPayload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            var _a;
                            console.log("deleteAppointment response", res.data);
                            return { success: !!(res.data && res.data.code === 1), error: (_a = res.data) === null || _a === void 0 ? void 0 : _a.error };
                        })
                            .catch(function (err) {
                            console.log("deleteAppointment error", err);
                            return { success: false, error: String(err) };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Booked appointment times ("HH:MM") for a provider on a date (pending/approved only).
 * Feeds the `booked` array consumed by computeAvailableSlots() inside getAvailability().
 */
function getBookedTimesForProvider(accountnumber, providerId, date) {
    return __awaiter(this, void 0, Promise, function () {
        var requestPayload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    requestPayload = {
                        Appointment: {
                            API_Meth: "GetAppointmentsForProviderDate",
                            accountnumber: accountnumber,
                            providerId: providerId,
                            date: date
                        }
                    };
                    console.log("getBookedTimesForProvider request", requestPayload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), requestPayload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("getBookedTimesForProvider response", res.data);
                            var rows = Array.isArray(res.data.records) ? res.data.records : [];
                            return rows.map(function (r) { return String(r.appointment_time).slice(0, 5); });
                        })
                            .catch(function (err) {
                            console.log("getBookedTimesForProvider error", err);
                            return [];
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Bulk-fetch active (non-cancelled) appointments for a set of patient ids.
 * Used to populate patientCardsStore's appointments list for the Patients card grid.
 */
function getAppointmentsForPatients(accountnumber, patientIds) {
    return __awaiter(this, void 0, Promise, function () {
        var requestPayload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (patientIds.length === 0)
                        return [2 /*return*/, []];
                    requestPayload = {
                        Appointment: {
                            API_Meth: "GetAppointmentsForPatients",
                            accountnumber: accountnumber,
                            patientIds: patientIds
                        }
                    };
                    console.log("getAppointmentsForPatients request", requestPayload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), requestPayload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("getAppointmentsForPatients response", res.data);
                            var rows = Array.isArray(res.data.records) ? res.data.records : [];
                            return rows.map(function (r) { return ({
                                id: String(r.id),
                                patientId: r.patient_id,
                                providerId: r.provider_id,
                                providerName: '',
                                date: r.appointment_date,
                                time: String(r.appointment_time).slice(0, 5),
                                meetingType: r.meeting_type,
                                reason: r.reason || '',
                                status: r.status,
                                scheduledBy: r.scheduled_by,
                                notifyEmail: !!Number(r.notify_email),
                                notifySms: !!Number(r.notify_sms),
                                durationMinutes: r.duration_minutes != null ? Number(r.duration_minutes) : undefined,
                                seriesId: r.series_id != null ? String(r.series_id) : undefined,
                                createdAt: r.created_at
                            }); });
                        })
                            .catch(function (err) {
                            console.log("getAppointmentsForPatients error", err);
                            return [];
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/* ============================================================================
 * AVAILABILITY ADMIN (used by the Scheduling component)
 * ========================================================================== */
/**
 * List a provider's weekly availability rules.
 * PHP:  GET /api/availability/rules?provider_id=prov-001
 *   SELECT * FROM provider_availability WHERE provider_id = :pid ORDER BY day_of_week, start_time;
 */
function getAvailabilityRules(accountnumber, providerId) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!USE_MOCK) return [3 /*break*/, 2];
                    payload = {
                        Schedule: {
                            API_Meth: "GetProviderAvailabilityRules",
                            accountnumber: accountnumber,
                            providerid: providerId
                        }
                    };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("Availability Rules Payload Response");
                            console.log(res.data);
                            var rows = Array.isArray(res.data.records) ? res.data.records : Array.isArray(res.data) ? res.data : [];
                            var availability = rows.map(function (r) {
                                var _a, _b, _c, _d, _e;
                                return ({
                                    id: r.id != null ? String(r.id) : null,
                                    accountnumber: r.accountnumber,
                                    providerId: (_a = r.provider_id) !== null && _a !== void 0 ? _a : null,
                                    dayOfWeek: Number(r.day_of_week),
                                    startTime: (_c = (_b = r.start_time) === null || _b === void 0 ? void 0 : _b.slice(0, 5)) !== null && _c !== void 0 ? _c : null,
                                    endTime: (_e = (_d = r.end_time) === null || _d === void 0 ? void 0 : _d.slice(0, 5)) !== null && _e !== void 0 ? _e : null,
                                    slotMinutes: Number(r.slot_minutes),
                                });
                            });
                            console.log("Mapped Availability:", availability);
                            return availability.filter(function (r) { return r.providerId === providerId; });
                        })
                            .catch(function (err) {
                            console.log("PostAvailabilityError", err);
                            return [];
                        })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: return [4 /*yield*/, delay(200)];
                case 3:
                    _a.sent();
                    return [2 /*return*/, mockAvailability.filter(function (r) { return r.providerId === providerId; })];
            }
        });
    });
}
/**
 * Create/update an availability rule.
 * PHP:  POST /api/availability/rules   (or PUT /api/availability/rules/{id})
 *   INSERT INTO provider_availability (provider_id, day_of_week, start_time, end_time, slot_minutes)
 *   VALUES (:pid, :dow, :start, :end, :slot)
 *   ON DUPLICATE KEY UPDATE start_time=:start, end_time=:end, slot_minutes=:slot;
 */
function saveAvailabilityRule(rule, actorId) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!USE_MOCK) return [3 /*break*/, 2];
                    payload = rule.id
                        ? {
                            Schedule: {
                                API_Meth: "UpdateProviderAvailabilityById",
                                accountnumber: rule.accountnumber,
                                id: rule.id,
                                dayOfWeek: rule.dayOfWeek,
                                startTime: rule.startTime,
                                endTime: rule.endTime,
                                slotMinutes: rule.slotMinutes,
                                // Backend authorization (scheduling permission scoping) -- see
                                // createAppointment's own actor_id comment above.
                                actor_id: actorId
                            }
                        }
                        : {
                            Schedule: {
                                API_Meth: "InsertProviderAvailabilityById",
                                accountnumber: rule.accountnumber,
                                provider_id: rule.providerId,
                                dayOfWeek: rule.dayOfWeek,
                                startTime: rule.startTime,
                                endTime: rule.endTime,
                                slotMinutes: rule.slotMinutes,
                                actor_id: actorId
                            }
                        };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("Availability Rules Payload Response");
                            console.log(res.data);
                            if (res.data && res.data.code === 1) {
                                return { success: true, id: String(res.data.id), providerid: rule.providerId };
                            }
                            return { success: false, id: '', providerid: rule.providerId };
                        })
                            .catch(function (err) {
                            console.log("PostAvailability Rules Error", err);
                            return { success: false, id: '', providerid: rule.providerId };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: return [2 /*return*/, { success: true, id: '', providerid: rule.providerId }];
            }
        });
    });
}
/**
 * Delete an availability rule.
 * PHP:  DELETE /api/availability/rules/{id}
 *   DELETE FROM provider_availability WHERE id = :id;
 */
function deleteAvailabilityRule(accountnumber, id, actorId) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!USE_MOCK) return [3 /*break*/, 2];
                    payload = {
                        Schedule: {
                            API_Meth: "DeleteProviderAvailabilityById",
                            accountnumber: accountnumber,
                            id: id,
                            // Backend authorization (scheduling permission scoping) -- see
                            // createAppointment's own actor_id comment above.
                            actor_id: actorId
                        }
                    };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("DeleteProviderAvailabilityById Payload Response");
                            console.log(res.data);
                            return { success: !!(res.data && res.data.code === 1) };
                        })
                            .catch(function (err) {
                            console.log("DeleteAvailabilityRuleError", err);
                            return { success: false };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: return [4 /*yield*/, delay(200)];
                case 3:
                    _a.sent();
                    console.log('[v0] deleteAvailabilityRule', { id: id });
                    return [2 /*return*/, { success: true }];
            }
        });
    });
}
/**
 * List a provider's time-off entries.
 * PHP:  GET /api/availability/time-off?provider_id=prov-001
 *   SELECT * FROM provider_time_off WHERE provider_id = :pid AND off_date >= CURDATE() ORDER BY off_date;
 */
function getTimeOff(accountnumber, providerId) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!USE_MOCK) return [3 /*break*/, 2];
                    payload = {
                        Schedule: {
                            API_Meth: "GetProviderTimeOff",
                            accountnumber: accountnumber,
                            providerid: providerId
                        }
                    };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("GetProviderTimeOff Payload Response");
                            console.log(res.data);
                            var rows = Array.isArray(res.data.records) ? res.data.records : Array.isArray(res.data) ? res.data : [];
                            return rows.map(function (r) { return ({
                                id: String(r.id),
                                providerId: r.provider_id,
                                offDate: r.off_date,
                                startTime: r.start_time || null,
                                endTime: r.end_time || null,
                                // Empty for a colleague: the API sends a time-off reason only to the
                                // caregiver and to a scheduling administrator (employee health
                                // information); everyone else sees that the time is taken, not why.
                                reason: r.reason || ''
                            }); });
                        })
                            .catch(function (err) {
                            console.log("GetProviderTimeOffError", err);
                            return [];
                        })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: return [4 /*yield*/, delay(200)];
                case 3:
                    _a.sent();
                    return [2 /*return*/, mockTimeOff.filter(function (t) { return t.providerId === providerId; })];
            }
        });
    });
}
/**
 * Create a time-off entry.
 * PHP:  POST /api/availability/time-off
 *   INSERT INTO provider_time_off (provider_id, off_date, start_time, end_time, reason)
 *   VALUES (:pid, :date, :start, :end, :reason);
 */
function saveTimeOff(entry, actorId) {
    return __awaiter(this, void 0, Promise, function () {
        var payload, data, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!USE_MOCK) return [3 /*break*/, 2];
                    payload = entry.id
                        ? {
                            Schedule: {
                                API_Meth: "UpdateTimeOff",
                                accountnumber: entry.accountnumber,
                                id: entry.id,
                                offDate: entry.offDate,
                                startTime: entry.startTime,
                                endTime: entry.endTime,
                                reason: entry.reason,
                                // Backend authorization (scheduling permission scoping) -- see
                                // createAppointment's own actor_id comment above.
                                actor_id: actorId
                            }
                        }
                        : {
                            Schedule: {
                                API_Meth: "SaveTimeOff",
                                accountnumber: entry.accountnumber,
                                providerid: entry.providerId,
                                offDate: entry.offDate,
                                startTime: entry.startTime,
                                endTime: entry.endTime,
                                reason: entry.reason,
                                actor_id: actorId
                            }
                        };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("SaveTimeOff Payload Response");
                            console.log(res.data);
                            if (res.data && res.data.code === 1) {
                                return { success: true, id: String(res.data.id) };
                            }
                            return { success: false, id: entry.id || '' };
                        })
                            .catch(function (err) {
                            console.log("SaveTimeOffError", err);
                            return { success: false, id: entry.id || '' };
                        })];
                case 1: return [2 /*return*/, _b.sent()];
                case 2:
                    if (!entry.id) return [3 /*break*/, 4];
                    return [4 /*yield*/, axios_1.default.put("".concat(API_BASE_URL, "/availability/time-off/").concat(entry.id), entry)];
                case 3:
                    _a = _b.sent();
                    return [3 /*break*/, 6];
                case 4: return [4 /*yield*/, axios_1.default.post("".concat(API_BASE_URL, "/availability/time-off"), entry)];
                case 5:
                    _a = _b.sent();
                    _b.label = 6;
                case 6:
                    data = (_a).data;
                    return [2 /*return*/, data];
            }
        });
    });
}
/**
 * Delete a time-off entry.
 * PHP:  DELETE /api/availability/time-off/{id}
 *   DELETE FROM provider_time_off WHERE id = :id;
 */
function deleteTimeOff(accountnumber, id, actorId) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!USE_MOCK) return [3 /*break*/, 2];
                    payload = {
                        Schedule: {
                            API_Meth: "DeleteTimeOff",
                            accountnumber: accountnumber,
                            id: id,
                            // Backend authorization (scheduling permission scoping) -- see
                            // createAppointment's own actor_id comment above.
                            actor_id: actorId
                        }
                    };
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { "Content-Type": "application/json" },
                            timeout: 15000,
                        })
                            .then(function (res) {
                            console.log("DeleteTimeOff Payload Response");
                            console.log(res.data);
                            return { success: !!(res.data && res.data.code === 1) };
                        })
                            .catch(function (err) {
                            console.log("DeleteTimeOffError", err);
                            return { success: false };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: return [4 /*yield*/, delay(200)];
                case 3:
                    _a.sent();
                    console.log('[v0] deleteTimeOff', { id: id });
                    return [2 /*return*/, { success: true }];
            }
        });
    });
}
/* ============================================================================
 * NURSE / HHA SCHEDULING
 * ========================================================================== */
/** All active nurse + HHA staff for the scheduling dropdown. */
function getSchedulingNurseStaff(accountnumber) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    payload = {
                        NurseSchedule: {
                            API_Meth: 'GetNurseStaff',
                            accountnumber: accountnumber
                        }
                    };
                    console.log('getSchedulingNurseStaff request', payload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { 'Content-Type': 'application/json' },
                            timeout: 15000
                        })
                            .then(function (res) {
                            console.log('getSchedulingNurseStaff response', res.data);
                            var rows = Array.isArray(res.data.records) ? res.data.records : [];
                            return rows.map(function (r) {
                                var _a, _b, _c, _d, _e, _f;
                                var ct = (_a = r.caregivetype) !== null && _a !== void 0 ? _a : '';
                                var isHHA = /home health aid/i.test(ct);
                                var credMap = {
                                    'Registered Nurse': 'RN',
                                    'Licensed Practical Nurse': 'LPN',
                                    'LPN': 'LPN',
                                    'Nurse Practitioner': 'NP'
                                };
                                var credential = isHHA ? 'HHA' : ((_b = credMap[ct]) !== null && _b !== void 0 ? _b : ct.split(' ').map(function (w) { return w[0]; }).join(''));
                                return {
                                    id: String(r.providerID),
                                    name: "".concat((_c = r.firstname) !== null && _c !== void 0 ? _c : '', " ").concat((_d = r.lastname) !== null && _d !== void 0 ? _d : '').trim(),
                                    credential: credential,
                                    category: (isHHA ? 'hha' : 'nurse'),
                                    specialty: ct,
                                    email: (_e = r.email) !== null && _e !== void 0 ? _e : '',
                                    phone: (_f = r.primaryphone) !== null && _f !== void 0 ? _f : ''
                                };
                            });
                        })
                            .catch(function (err) {
                            console.log('getSchedulingNurseStaff error', err);
                            return [];
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/** Fetch shift settings for a group key ('nurse' or 'hha'). */
function getNurseShiftSettings(accountnumber, groupKey) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    payload = {
                        NurseSchedule: {
                            API_Meth: 'GetShiftSettings',
                            accountnumber: accountnumber,
                            groupKey: groupKey
                        }
                    };
                    console.log('getNurseShiftSettings request', payload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { 'Content-Type': 'application/json' },
                            timeout: 15000
                        })
                            .then(function (res) {
                            console.log('getNurseShiftSettings response', res.data);
                            var rows = Array.isArray(res.data.records) ? res.data.records : [];
                            if (!rows.length)
                                return null;
                            var r = rows[0];
                            return {
                                groupKey: r.group_key,
                                minIncrementMinutes: Number(r.min_increment_minutes),
                                maxIncrementMinutes: Number(r.max_increment_minutes),
                                stepMinutes: Number(r.step_minutes),
                                maxConsecutiveHours: Number(r.max_consecutive_hours)
                            };
                        })
                            .catch(function (err) {
                            console.log('getNurseShiftSettings error', err);
                            return null;
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/** Upsert shift settings for a group (one row per accountnumber+groupKey). */
function saveNurseShiftSettings(accountnumber, settings) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    payload = {
                        NurseSchedule: {
                            API_Meth: 'SaveShiftSettings',
                            accountnumber: accountnumber,
                            groupKey: settings.groupKey,
                            minMinutes: settings.minIncrementMinutes,
                            maxMinutes: settings.maxIncrementMinutes,
                            stepMinutes: settings.stepMinutes,
                            maxConsecutiveHours: settings.maxConsecutiveHours
                        }
                    };
                    console.log('saveNurseShiftSettings request', payload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { 'Content-Type': 'application/json' },
                            timeout: 15000
                        })
                            .then(function (res) {
                            console.log('saveNurseShiftSettings response', res.data);
                            return { success: !!(res.data && res.data.code === 1) };
                        })
                            .catch(function (err) {
                            console.log('saveNurseShiftSettings error', err);
                            return { success: false };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
var DEFAULT_MAX_CONCURRENT_CAREGIVERS = 2;
/** Fetch practice-wide scheduling UI settings (one row per accountnumber). */
function getSchedulingUiSettings(accountnumber) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    payload = {
                        SchedulingSettings: { API_Meth: 'GetSchedulingUiSettings', accountnumber: accountnumber }
                    };
                    console.log('getSchedulingUiSettings request', payload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { 'Content-Type': 'application/json' },
                            timeout: 15000
                        })
                            .then(function (res) {
                            console.log('getSchedulingUiSettings response', res.data);
                            var rows = Array.isArray(res.data.records) ? res.data.records : [];
                            var r = rows[0];
                            return {
                                enableEnvironmentTabs: r ? !!Number(r.enable_environment_tabs) : false,
                                maxConcurrentCaregivers: r && r.max_concurrent_caregivers != null ? Number(r.max_concurrent_caregivers) : DEFAULT_MAX_CONCURRENT_CAREGIVERS,
                                showSupportingCards: r ? !!Number(r.show_calendar_supporting_cards) : false
                            };
                        })
                            .catch(function (err) {
                            console.log('getSchedulingUiSettings error', err);
                            return { enableEnvironmentTabs: false, maxConcurrentCaregivers: DEFAULT_MAX_CONCURRENT_CAREGIVERS, showSupportingCards: false };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/** Upsert practice-wide scheduling UI settings. */
function saveSchedulingUiSettings(accountnumber, settings) {
    return __awaiter(this, void 0, Promise, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    payload = {
                        SchedulingSettings: {
                            API_Meth: 'SaveSchedulingUiSettings',
                            accountnumber: accountnumber,
                            enableEnvironmentTabs: settings.enableEnvironmentTabs,
                            maxConcurrentCaregivers: settings.maxConcurrentCaregivers,
                            showSupportingCards: settings.showSupportingCards
                        }
                    };
                    console.log('saveSchedulingUiSettings request', payload);
                    return [4 /*yield*/, axios_1.default
                            .post("".concat(API_BASE_URL, "/tswebhook.php"), payload, {
                            headers: { 'Content-Type': 'application/json' },
                            timeout: 15000
                        })
                            .then(function (res) {
                            console.log('saveSchedulingUiSettings response', res.data);
                            return { success: !!(res.data && res.data.code === 1) };
                        })
                            .catch(function (err) {
                            console.log('saveSchedulingUiSettings error', err);
                            return { success: false };
                        })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
